import { headingProp, wysiwygProp } from "core-web/utils/proptypes/modules/textProps";

import ArrowIcon from "theme/components/icons/ArrowIcon";
import BackgroundSwitch from "theme/components/BackgroundSwitch";
import Div from "theme/layouts/Div";
// import FlexibleWrapper from "theme/components/wrappers/FlexibleWrapper";
import Link from "core-web/components/Link";
import PropTypes from "prop-types";
import React from "react";
// import ScrollToButton from "theme/components/buttons/functionalButtons/ScrollToButton";
import TextStyles from "theme/components/text/TextStyles";
import ThemeButton from "theme/components/buttons/ThemeButton";
import Waterfall from "theme/layouts/Waterfall";
import Wysiwyg from "theme/libs/wordpress/components/Wysiwyg";
import styled from "theme/libs/styled";

const excludedProps = ["isHero", "animateBackgroundImage"];

// If styling is changed, it may be needed to be changed in HoverOverlayContent.js as well to match this.

const FlexibleWrapper = styled("div", {
    shouldForwardProp: (prop) => excludedProps.indexOf(prop) === -1,
})`
    position: relative;
    display: flex;
    height: 100%;

    padding: ${({ theme, isHero }) => (isHero ? `calc(${theme.variables.headerHeight}px + 20px) 20px 24px` : "16px")};

    ${({ theme }) => theme.above.tabletLg} {
        padding: ${({ theme, isHero }) =>
            isHero ? `calc(${theme.variables.headerHeight}px + 24px) 48px 48px` : "24px"};
    }

    ${({ animateBackgroundImage }) =>
        animateBackgroundImage &&
        `
        .background-wrapper > div {
            transition: all 800ms ease //transitions.secondarySlow
        }

        :hover {
            .background-wrapper > div {
                scale: 1.05;
            }
        }
    `}
`;

const BackgroundWrapper = styled("div", {
    shouldForwardProp: (prop) => excludedProps.indexOf(prop) === -1,
})`
    position: absolute;
    inset: 0;
    border-radius: ${({ isHero }) => (isHero ? "0 0 12px 12px" : "12px")};
    background-color: ${({ theme }) => theme.colors.neutral_200};
    overflow: hidden;
`;

const ContentWrapper = styled(Waterfall)`
    width: min(862px, 100%);
    gap: 10px;
    z-index: 1;

    &[data-hero="false"] {
        position: sticky;
        bottom: 24px;
        gap: 12px;
    }
`;

/**
 * Main contentBlock used to display heading, text and button on a background.
 *
 * @param {string} button - Button object with text, url & theme.
 * @param {string} heading - Heading as a string.
 * @param {bool} isHero - Bool to use for hero or non-hero exclusive styling.
 * @param {array} paragraph - Paragraph-text as a wysiwyg-array.
 * @param {string} playButtonLabel - text for playbutton in videomodule.
 * @param {string} playButtonTheme - Theme for videomodule playbutton.
 * @param {string} tag - Small tag displayed above heading.
 * @param {string} textColor - color of text and scrollindicator.
 * @param {string} WrapperLink - Link for whole module, should not be used in combination with buttonLink. You can still use button though.
 * @param {*} rest - Props to pass to FlexibleWrapper.
 */

const Flexible = ({
    isHero,
    button,
    hasScrollTo,
    heading,
    paragraph,
    playButtonLabel,
    playButtonTheme,

    tag,
    textColor,
    mediaQuery,
    backgroundType,
    background,
    layout,
    scrollToClassName,
    paragraphTextStyle,
    xAlignContent,
    yAlignContent,
    ...rest
}) => {
    const validTags = ["url", "i", "b", "u", "ul", "ol", "li", "span"];

    const verticalAlignment = {
        top: "start",
        middle: "center",
        bottom: "end",
    };

    const type = backgroundType ? backgroundType : typeof background === "object" ? background.type : "color";

    return (
        <FlexibleWrapper
            isHero={isHero}
            animateBackgroundImage={type === "image" && !isHero}
            alignItems={verticalAlignment[yAlignContent]}
            justifyContent={xAlignContent}
        >
            <BackgroundWrapper className="background-wrapper" isHero={isHero}>
                <BackgroundSwitch type={type} background={background} mediaQuery={mediaQuery} /* loading={loading} */ />
            </BackgroundWrapper>

            <ContentWrapper data-hero={isHero}>
                {tag && (
                    <TextStyles
                        as="span"
                        textStyles={["Myriad Pro/16-100", null, null, null, "Myriad Pro/20-100"]}
                        color={textColor}
                        mb={["6px", null, null, null, "6px"]}
                    >
                        {tag}
                    </TextStyles>
                )}

                {heading && (
                    <TextStyles
                        as={heading.type || (isHero ? "h1" : "h3")}
                        textStyles={heading.textStyle}
                        color={heading.color || textColor}
                    >
                        {heading.text}
                    </TextStyles>
                )}

                {paragraph && (
                    <Div width="min(515px, 100%)">
                        <Wysiwyg
                            data={paragraph}
                            textComponent={(props) => (
                                <TextStyles textStyles={paragraphTextStyle} color={textColor} {...props} />
                            )}
                            validTags={validTags}
                        />
                    </Div>
                )}

                {button?.label && button.link && (
                    <ThemeButton as={Link} to={button.link} buttonTheme={button.theme} mt="14px">
                        {button.label}
                        <ArrowIcon />
                    </ThemeButton>
                )}
            </ContentWrapper>
        </FlexibleWrapper>
    );
};

Flexible.propTypes = {
    button: PropTypes.object,
    columnLayout: PropTypes.string,
    hasScrollTo: PropTypes.bool,
    heading: headingProp,
    isHero: PropTypes.bool,
    paragraph: PropTypes.oneOfType([PropTypes.bool, wysiwygProp]),
    playButtonLabel: PropTypes.string,
    playButtonTheme: PropTypes.string,
    scrollToClassName: PropTypes.string,
    tag: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    textColor: PropTypes.string,
};

Flexible.defaultProps = {
    button: undefined,
    columnLayout: "default",
    hasScrollTo: false,
    heading: undefined,
    isHero: false,
    paragraph: undefined,
    playButtonLabel: "",
    playButtonTheme: "",
    scrollToClassName: "",
    tag: undefined,
    textColor: "#FFFFFF",
};

export default Flexible;
